'use client';

import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useTargetId, useTargetType } from '../store/projectStore';
import { isAppTarget } from '../utils/target-scope.utils';
import { useInvalidateDiscoveryAssets } from './useAssetsDiscoveryRefresh';
import { ASSETS_SEARCH_QUERY_KEY } from './api/useAssetsSearch';

export function useTargetChangeSync() {
  const queryClient = useQueryClient();
  const targetId = useTargetId();
  const targetType = useTargetType();
  const invalidateDiscoveryAssets = useInvalidateDiscoveryAssets();
  const previousTargetRef = useRef<{
    targetId: string;
    targetType: TargetTypeType;
  } | null>(null);

  useEffect(() => {
    const previousTarget = previousTargetRef.current;
    previousTargetRef.current = { targetId, targetType };

    if (!previousTarget) {
      return;
    }

    if (
      previousTarget.targetId === targetId &&
      previousTarget.targetType === targetType
    ) {
      return;
    }

    if (previousTarget.targetId && isAppTarget(previousTarget.targetType)) {
      void queryClient.removeQueries({
        queryKey: [ASSETS_SEARCH_QUERY_KEY, previousTarget.targetId],
        exact: false,
      });
    }

    if (targetId && isAppTarget(targetType)) {
      void invalidateDiscoveryAssets({ applicationId: targetId });
      void queryClient.invalidateQueries({
        queryKey: ['application-credentials', targetId],
        exact: false,
      });
    }
  }, [targetId, targetType, queryClient, invalidateDiscoveryAssets]);
}
